import classNames from 'classnames'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { ExperiencedClassificationTest } from './ExperiencedClassificationTest/ExperiencedClassificationTest'
import ProfessionalClassificationTest from './ProfessionalClassificationTest/ProfessionalClassificationTest'

import styles from './ClientClassificationPage.module.scss'

const PolishClassificationTest: React.FC<ClassificationProps> = ({
  selectedTest,
  setSelectedTest,
}) => {
  const { t } = useTranslation()

  if (selectedTest === 'Professional') {
    return <ProfessionalClassificationTest handleOkOnClick={() => setSelectedTest(undefined)} />
  } else if (selectedTest === 'Experienced') {
    return <ExperiencedClassificationTest handleOkOnClick={() => setSelectedTest(undefined)} />
  }

  return (
    <>
      <div className={styles.header} onClick={() => setSelectedTest('Professional')}>
        <Body1 isParagraph medium>
          {t('Profile.Application Form to be Classified as a Professional Client')}
        </Body1>
        <ChevronIcon.Right size={24} />
      </div>
      <div className={styles.header} onClick={() => setSelectedTest('Experienced')}>
        <Body1 isParagraph medium>
          {t('Profile.Application Form to be Classified as an Experienced Retail Client')}
        </Body1>
        <ChevronIcon.Right size={24} />
      </div>
    </>
  )
}

type ClassificationTestType = 'Professional' | 'Experienced' | undefined

interface ClassificationProps {
  selectedTest: ClassificationTestType | undefined
  setSelectedTest: (selectedTest: ClassificationTestType) => void
}

const ClassificationTest: React.FC<ClassificationProps> = ({ selectedTest, setSelectedTest }) => {
  const { t } = useTranslation()

  if (selectedTest) {
    return <ProfessionalClassificationTest handleOkOnClick={() => setSelectedTest(undefined)} />
  }

  return (
    <div
      className={classNames(styles.header, 'is-clickable mt-4')}
      onClick={() => setSelectedTest('Professional')}
    >
      <Body1 isParagraph medium>
        {t('Profile.Application Form to be Classified as a Professional Client')}
      </Body1>
      <ChevronIcon.Right size={24} />
    </div>
  )
}

export const ClientClassificationPage = () => {
  useScrollToTop()
  const [selectedTest, setSelectedTest] = useState<ClassificationTestType>()

  const { account } = useAccountReadContext()

  const isPolish =
    account?.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_EU &&
    !!account?.addresses.length &&
    account.addresses[0].country?.id === 'POL'

  const handleSetSelectedTest = (testType: ClassificationTestType) => {
    setSelectedTest(testType)
  }

  const { t } = useTranslation()
  return (
    <div className={styles.wrapper}>
      <PageSection
        title={t('Profile.Client Classification')}
        onGoBack={selectedTest ? () => setSelectedTest(undefined) : undefined}
      />
      {isPolish ? (
        <PolishClassificationTest
          setSelectedTest={handleSetSelectedTest}
          selectedTest={selectedTest}
        />
      ) : (
        <ClassificationTest setSelectedTest={handleSetSelectedTest} selectedTest={selectedTest} />
      )}
    </div>
  )
}
